import { useId } from 'commons/hooks/use-id/use-id.hook';
import React, { useEffect, useRef, useState } from 'react';

export interface UseAsyncTooltipMountOptions {
    mountDelayMs?: number;
    hideDelayMs?: number;
    shouldShowTooltip?: (ev: React.MouseEvent<HTMLElement>) => boolean;
}

const DEFAULT_MOUNT_DELAY = 0;
const DEFAULT_HIDE_DELAY = 200;

export const useAsyncTooltipMount = (options: UseAsyncTooltipMountOptions = {}) => {
    const { mountDelayMs = DEFAULT_MOUNT_DELAY, hideDelayMs = DEFAULT_HIDE_DELAY, shouldShowTooltip } = options;

    const tooltipAnchorId = useId();

    const [isMountedTooltip, setIsMountedTooltip] = useState(false);
    const [isVisibleTooltip, setIsVisibleTooltip] = useState(false);

    const mountTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearMountTimer = () => {
        if (mountTimeoutRef.current) {
            clearTimeout(mountTimeoutRef.current);
            mountTimeoutRef.current = null;
        }
    };

    const clearHideTimer = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };

    useEffect(
        () => () => {
            clearMountTimer();
            clearHideTimer();
        },
        []
    );

    const showTooltip = (ev: React.MouseEvent<HTMLElement>) => {
        clearHideTimer();

        if (shouldShowTooltip && !shouldShowTooltip(ev)) {
            return;
        }

        if (isMountedTooltip) {
            setIsVisibleTooltip(true);
            return;
        }

        clearMountTimer();
        mountTimeoutRef.current = setTimeout(() => {
            setIsMountedTooltip(true);
            requestAnimationFrame(() => setIsVisibleTooltip(true));
        }, mountDelayMs);
    };

    const hideTooltip = () => {
        clearMountTimer();
        clearHideTimer();
        hideTimeoutRef.current = setTimeout(() => {
            setIsVisibleTooltip(false);
            setIsMountedTooltip(false);
        }, hideDelayMs);
    };

    return {
        tooltipAnchorId,
        isMountedTooltip,
        isVisibleTooltip,
        onMouseEnter: showTooltip,
        onMouseLeave: hideTooltip,
    };
};
